import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabase";
import { FileText, Plus, Trash2, Edit, Upload, Check, X, AlertTriangle, Download, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

// Definición de tipos
interface Catalog {
  id: string;
  title: string;
  description: string;
  pdfUrl: string;
  category: string;
  tags: string[];
  date: string;
}

export function CatalogsTab() {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Estado para el formulario
  const [formData, setFormData] = useState<Omit<Catalog, 'id'>>({
    title: "",
    description: "",
    pdfUrl: "",
    category: "General",
    tags: [],
    date: new Date().toISOString().split('T')[0]
  });

  const { toast } = useToast();

  // Cargar catálogos al montar el componente
  useEffect(() => {
    loadCatalogs();
  }, []);

  // Función para cargar los catálogos
  const loadCatalogs = async () => {
    try {
      setLoading(true);

      // Opción 1: Si tienes una tabla de catálogos en Supabase
      const { data, error } = await supabase
        .from('catalogs')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      if (data) {
        // Transformar los datos para que coincidan con la interfaz Catalog
        const formattedCatalogs = data.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          pdfUrl: item.pdf_url, // Transformar pdf_url a pdfUrl
          category: item.category,
          tags: item.tags || [],
          date: item.date
        }));
        setCatalogs(formattedCatalogs);
      }

      // Opción 2: Si los datos están en un archivo estático (como en catalogs.tsx)
      // const staticCatalogs = [
      //   {
      //     id: "general",
      //     title: "Catálogo general",
      //     description: "Nuestro catálogo completo con toda nuestra gama de maquinaria industrial para el sector alimentario.",
      //     pdfUrl: "Catalogo_innotec_digital_nuevo_compressed.pdf",
      //     category: "General",
      //     tags: ["maquinaria", "industrial", "alimentario", "general"],
      //     date: "2023-12-01"
      //   },
      //   {
      //     id: "grandes",
      //     title: "Maquinaria de proceso para gran industria",
      //     description: "Catálogo especializado en nuestra línea de maquinaria industrial de gran formato para procesamiento alimentario.",
      //     pdfUrl: "catalogo-grandes-comprimido.pdf",
      //     category: "Especializado",
      //     tags: ["maquinaria", "industrial", "grande", "procesamiento"],
      //     date: "2023-11-15"
      //   }
      // ];
      // setCatalogs(staticCatalogs);

    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron cargar los catálogos"
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para filtrar catálogos según la búsqueda
  const filteredCatalogs = catalogs.filter(catalog => {
    const searchLower = searchQuery.toLowerCase();
    return (
      catalog.title.toLowerCase().includes(searchLower) ||
      catalog.description.toLowerCase().includes(searchLower) ||
      catalog.category.toLowerCase().includes(searchLower) ||
      catalog.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  // Función para manejar la adición de tags
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim().toLowerCase()]
      });
      setTagInput("");
    }
  };

  // Función para eliminar un tag
  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  // Función para manejar la selección de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Función para subir el archivo a Supabase Storage
  const uploadFile = async (): Promise<string> => {
    if (!file) {
      throw new Error("No hay archivo para subir");
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generar un nombre único para el archivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;

      // Simular progreso de carga
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      // Subir archivo a Supabase Storage
      const { data, error } = await supabase.storage
        .from('pdfs')
        .upload(fileName, file);

      if (error) throw error;

      clearInterval(interval);
      setUploadProgress(100);

      return fileName;
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Función para guardar un nuevo catálogo
  const handleSaveCatalog = async () => {
    try {
      if (!formData.title || !formData.description || (!formData.pdfUrl && !file)) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Por favor completa todos los campos obligatorios"
        });
        return;
      }

      let pdfUrl = formData.pdfUrl;

      // Si hay un archivo seleccionado, subirlo primero
      if (file) {
        pdfUrl = await uploadFile();
      }

      // Guardar el catálogo en la base de datos
      const { data, error } = await supabase
        .from('catalogs')
        .insert([{
          title: formData.title,
          description: formData.description,
          pdf_url: pdfUrl, // Usar pdf_url para Supabase
          category: formData.category,
          tags: formData.tags,
          date: formData.date || new Date().toISOString().split('T')[0]
        }])
        .select();

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Catálogo guardado correctamente"
      });

      // Actualizar la lista de catálogos
      loadCatalogs();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error("Error al guardar el catálogo:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo guardar el catálogo"
      });
    }
  };

  // Función para actualizar un catálogo existente
  const handleUpdateCatalog = async () => {
    try {
      if (!selectedCatalog) return;

      let pdfUrl = formData.pdfUrl;

      // Si hay un archivo seleccionado, subirlo primero
      if (file) {
        pdfUrl = await uploadFile();
      }

      // Actualizar el catálogo en la base de datos
      const { error } = await supabase
        .from('catalogs')
        .update({
          title: formData.title,
          description: formData.description,
          pdf_url: pdfUrl, // Usar pdf_url para Supabase
          category: formData.category,
          tags: formData.tags,
          date: formData.date
        })
        .eq('id', selectedCatalog.id);

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Catálogo actualizado correctamente"
      });

      // Actualizar la lista de catálogos
      loadCatalogs();
      setShowEditModal(false);
      resetForm();
    } catch (error) {
      console.error("Error al actualizar el catálogo:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el catálogo"
      });
    }
  };

  // Función para eliminar un catálogo
  const handleDeleteCatalog = async () => {
    try {
      if (!selectedCatalog) return;

      // Eliminar el catálogo de la base de datos
      const { error } = await supabase
        .from('catalogs')
        .delete()
        .eq('id', selectedCatalog.id);

      if (error) throw error;

      // Opcionalmente, eliminar también el archivo de Storage
      if (selectedCatalog.pdfUrl) {
        const { error: storageError } = await supabase.storage
          .from('pdfs')
          .remove([selectedCatalog.pdfUrl]);

        if (storageError) {
          console.error("Error al eliminar el archivo:", storageError);
        }
      }

      toast({
        title: "Éxito",
        description: "Catálogo eliminado correctamente"
      });

      // Actualizar la lista de catálogos
      loadCatalogs();
      setShowDeleteModal(false);
      setSelectedCatalog(null);
    } catch (error) {
      console.error("Error al eliminar el catálogo:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el catálogo"
      });
    }
  };

  // Función para abrir el modal de edición
  const handleEdit = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    setFormData({
      title: catalog.title,
      description: catalog.description,
      pdfUrl: catalog.pdfUrl,
      category: catalog.category,
      tags: catalog.tags,
      date: catalog.date
    });
    setShowEditModal(true);
  };

  // Función para abrir el modal de eliminación
  const handleDelete = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    setShowDeleteModal(true);
  };

  // Función para previsualizar un PDF
  const handlePreview = (catalog: Catalog) => {
    const { data } = supabase.storage
      .from('pdfs')
      .getPublicUrl(catalog.pdfUrl);

    if (data?.publicUrl) {
      window.open(data.publicUrl, '_blank');
    }
  };

  // Función para resetear el formulario
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      pdfUrl: "",
      category: "General",
      tags: [],
      date: new Date().toISOString().split('T')[0]
    });
    setFile(null);
    setTagInput("");
    setUploadProgress(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Catálogos</h2>
          <p className="text-white/60">
            Gestiona los catálogos que se muestran en la sección pública
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Input
              placeholder="Buscar catálogos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white"
            />
            <FileText className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90 text-black font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo catálogo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      ) : filteredCatalogs.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
          <FileText className="h-12 w-12 text-white/20 mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-white mb-2">No hay catálogos</h3>
          <p className="text-white/60 max-w-md mx-auto mb-6">
            {searchQuery
              ? "No se encontraron catálogos que coincidan con tu búsqueda"
              : "Aún no has añadido ningún catálogo. ¿Por qué no creas uno nuevo?"}
          </p>
          <Button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90 text-black font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Añadir catálogo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCatalogs.map((catalog) => (
            <Card key={catalog.id} className="bg-white/5 border-white/10 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] w-full"></div>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{catalog.title}</h3>
                    <Badge variant="outline" className="bg-white/10 text-white border-white/20 mb-2">
                      {catalog.category}
                    </Badge>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handlePreview(catalog)}
                      className="h-8 w-8 rounded-full hover:bg-white/10"
                    >
                      <Eye className="h-4 w-4 text-white/70" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(catalog)}
                      className="h-8 w-8 rounded-full hover:bg-white/10"
                    >
                      <Edit className="h-4 w-4 text-white/70" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(catalog)}
                      className="h-8 w-8 rounded-full hover:bg-white/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4 text-white/70" />
                    </Button>
                  </div>
                </div>

                <p className="text-white/70 text-sm line-clamp-2 mb-3">{catalog.description}</p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {catalog.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="bg-white/10 text-white/80 border-none text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="flex justify-between items-center text-xs text-white/50">
                  <span>PDF: {catalog.pdfUrl ? catalog.pdfUrl.split('/').pop()?.substring(0, 15) + '...' : 'Sin archivo'}</span>
                  <span>{new Date(catalog.date).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal para añadir nuevo catálogo */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#16171B] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Añadir nuevo catálogo</DialogTitle>
            <DialogDescription className="text-white/60">
              Completa el formulario para agregar un nuevo catálogo
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej. Catálogo general"
                className="bg-white/5 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descripción del catálogo"
                className="bg-white/5 border-white/10 min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#16171B] border-white/10">
                    <SelectGroup>
                      <SelectLabel>Categorías</SelectLabel>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Especializado">Especializado</SelectItem>
                      <SelectItem value="Técnico">Técnico</SelectItem>
                      <SelectItem value="Maquinaria">Maquinaria</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Fecha</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Etiquetas</Label>
              <div className="flex gap-2">
                <Input
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Añadir etiqueta"
                  className="bg-white/5 border-white/10 flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="secondary"
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  Añadir
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {formData.tags.map(tag => (
                    <Badge
                      key={tag}
                      className="bg-white/10 hover:bg-white/20 border-none text-white gap-1"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-red-400"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pdfFile">Archivo PDF</Label>
              <div className="border border-dashed border-white/20 rounded-lg p-4 bg-white/5">
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload className="h-10 w-10 text-white/30 mb-2" />
                  <p className="text-sm font-medium text-white mb-1">Arrastra un archivo PDF o haz clic para seleccionar</p>
                  <p className="text-xs text-white/50 mb-4">PDF (Máx. 50MB)</p>

                  <Input
                    id="pdfFile"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('pdfFile')?.click()}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Seleccionar archivo
                  </Button>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-white/10 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-white" />
                        <span className="text-sm text-white">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFile(null)}
                        className="h-6 w-6 rounded-full hover:bg-white/10 hover:text-red-400"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="mt-4">
                    <div className="w-full bg-white/10 rounded-full h-2.5">
                      <div
                        className="bg-[#FCAA0D] h-2.5 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="flex justify-end mt-1">
                      <span className="text-xs text-white/50">{uploadProgress}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCatalog}
              className="bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90 text-black font-medium"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Guardar catálogo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para editar catálogo */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#16171B] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Editar catálogo</DialogTitle>
            <DialogDescription className="text-white/60">
              Actualiza la información del catálogo
            </DialogDescription>
          </DialogHeader>

          {/* Contenido similar al modal de añadir, pero con datos pre-cargados */}
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Título</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white/5 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Descripción</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-category">Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#16171B] border-white/10">
                    <SelectGroup>
                      <SelectLabel>Categorías</SelectLabel>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Especializado">Especializado</SelectItem>
                      <SelectItem value="Técnico">Técnico</SelectItem>
                      <SelectItem value="Maquinaria">Maquinaria</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-date">Fecha</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tags">Etiquetas</Label>
              <div className="flex gap-2">
                <Input
                  id="edit-tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Añadir etiqueta"
                  className="bg-white/5 border-white/10 flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="secondary"
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  Añadir
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {formData.tags.map(tag => (
                    <Badge
                      key={tag}
                      className="bg-white/10 hover:bg-white/20 border-none text-white gap-1"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-red-400"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="edit-pdfFile">Archivo PDF</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/50">PDF actual:</span>
                  <Badge variant="outline" className="bg-white/10 border-white/20 text-white">
                    {formData.pdfUrl ? formData.pdfUrl.split('/').pop() || 'Ninguno' : 'Ninguno'}
                  </Badge>
                </div>
              </div>
              <div className="border border-dashed border-white/20 rounded-lg p-4 bg-white/5">
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload className="h-10 w-10 text-white/30 mb-2" />
                  <p className="text-sm font-medium text-white mb-1">Arrastra un nuevo PDF o haz clic para seleccionar</p>
                  <p className="text-xs text-white/50 mb-4">Solo si deseas cambiar el actual (Máx. 50MB)</p>

                  <Input
                    id="edit-pdfFile"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('edit-pdfFile')?.click()}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Seleccionar nuevo archivo
                  </Button>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-white/10 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-white" />
                        <span className="text-sm text-white">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFile(null)}
                        className="h-6 w-6 rounded-full hover:bg-white/10 hover:text-red-400"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="mt-4">
                    <div className="w-full bg-white/10 rounded-full h-2.5">
                      <div
                        className="bg-[#FCAA0D] h-2.5 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="flex justify-end mt-1">
                      <span className="text-xs text-white/50">{uploadProgress}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateCatalog}
              className="bg-gradient-to-r from-[#FCAA0D] to-[#FF8A00] hover:opacity-90 text-black font-medium"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                  Actualizando...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Actualizar catálogo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación para eliminar */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#16171B] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Confirmación</DialogTitle>
            <DialogDescription className="text-white/60">
              ¿Estás seguro de que deseas eliminar este catálogo?
            </DialogDescription>
          </DialogHeader>

          {selectedCatalog && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-900/30 rounded-lg mb-4">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <p className="text-sm text-white">Esta acción no se puede deshacer</p>
              </div>

              <div className="space-y-2">
                <p className="text-white font-semibold">{selectedCatalog.title}</p>
                <p className="text-white/70 text-sm">{selectedCatalog.description}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              onClick={handleDeleteCatalog}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
